import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Zap } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../lib/authStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PACKS = [
  {
    usd: 19,  battles: 10,  name: 'Starter',
    sub: '10 tests · $1.90 per test',
    popular: false,
  },
  {
    usd: 49,  battles: 30,  name: 'Growth',
    sub: '30 tests · $1.63 per test',
    popular: true,
  },
  {
    usd: 149, battles: 100, name: 'Agency',
    sub: '100 tests · $1.49 per test',
    popular: false,
  },
];

function openRazorpay({ order, pack, user, onSuccess, onError }) {
  const options = {
    key:         order.key_id,
    amount:      order.amount,
    currency:    'USD',
    name:        'eonik',
    description: `${pack.battles} battle${pack.battles > 1 ? 's' : ''}`,
    order_id:    order.order_id,
    prefill:     order.prefill,
    theme:       { color: '#18181B' },
    handler: async (response) => {
      try {
        const { data } = await axios.post(`${BACKEND_URL}/api/payments/verify`, {
          razorpay_order_id:   response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature:  response.razorpay_signature,
        });
        onSuccess?.(data.credits);
      } catch (e) {
        onError?.(e.response?.data?.detail || 'Payment verification failed.');
      }
    },
    modal: { ondismiss: () => {} },
  };

  const rzp = new window.Razorpay(options);
  rzp.on('payment.failed', (r) => onError?.(r.error?.description || 'Payment failed.'));
  rzp.open();
}

export default function PricingModal({ open, onClose, onPurchased }) {
  const [loading, setLoading] = useState(null);
  const [error,   setError]   = useState('');
  const { user, refreshUser } = useAuthStore();

  const buy = async (pack) => {
    if (!window.Razorpay) {
      setError('Payment system not loaded. Refresh and try again.');
      return;
    }
    setLoading(pack.usd);
    setError('');
    try {
      const { data: order } = await axios.post(
        `${BACKEND_URL}/api/payments/create-order`,
        { amount: pack.usd }
      );
      openRazorpay({
        order, pack, user,
        onSuccess: async (battle_credits) => {
          setLoading(null);
          await refreshUser();
          onPurchased?.(battle_credits);
          onClose();
        },
        onError: (msg) => {
          setLoading(null);
          setError(msg);
        },
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create order.');
      setLoading(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(0,0,0,0.18)',
              backdropFilter: 'blur(4px)',
            }}
          />

          <div
            key="panel-shell"
            style={{
              position: 'fixed', inset: 0, zIndex: 201,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0,  scale: 1 }}
            exit={{ opacity: 0,  y: 8,   scale: 0.97 }}
            transition={{ duration: 0.22 }}
            style={{
              pointerEvents: 'auto',
              position: 'relative',
              width: 400,
              background: '#fff',
              border: '1px solid #E4E4E7',
              borderRadius: 20,
              padding: '36px 32px 28px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
            }}
          >
            <button
              onClick={onClose}
              style={{
                position: 'absolute', top: 16, right: 16,
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#A1A1AA', display: 'flex',
              }}
            >
              <X size={16} />
            </button>

            <h2 style={{
              fontSize: 20, fontWeight: 700, color: '#09090B',
              letterSpacing: '-0.03em', margin: '0 0 4px',
            }}>
              Buy A/B tests
            </h2>
            <p style={{ fontSize: 13, color: '#71717A', margin: '0 0 28px' }}>
              Run fast, evidence-backed A/B tests before you spend on media.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {PACKS.map(pack => (
                <div
                  key={pack.usd}
                  style={{
                    position: 'relative',
                    border: `1.5px solid ${pack.popular ? '#18181B' : '#E4E4E7'}`,
                    borderRadius: 12,
                    padding: '16px 18px',
                    background: pack.popular ? '#FAFAFA' : '#fff',
                  }}
                >
                  {pack.popular && (
                    <span style={{
                      position: 'absolute', top: -10, right: 14,
                      background: '#18181B', color: '#fff',
                      fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.08em',
                      padding: '3px 8px', borderRadius: 100,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}>
                      BEST VALUE
                    </span>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{
                        fontSize: 15, fontWeight: 700, color: '#09090B',
                        margin: '0 0 1px',
                      }}>
                        {pack.name}
                        <span style={{ fontSize: 12, fontWeight: 400, color: '#71717A', marginLeft: 6 }}>
                          · {pack.battles} tests
                        </span>
                      </p>
                      <p style={{ fontSize: 11, color: '#A1A1AA', margin: 0, fontFamily: "'IBM Plex Mono', monospace" }}>
                        {pack.sub}
                      </p>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{
                        fontSize: 22, fontWeight: 800, color: '#09090B',
                        letterSpacing: '-0.03em',
                      }}>
                        ${pack.usd}
                      </span>
                      <button
                        onClick={() => buy(pack)}
                        disabled={!!loading}
                        style={{
                          padding: '8px 16px',
                          background: loading === pack.usd ? '#F4F4F5' : '#18181B',
                          color: loading === pack.usd ? '#A1A1AA' : '#fff',
                          border: 'none', borderRadius: 8,
                          fontSize: 13, fontWeight: 600,
                          cursor: loading ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', gap: 5,
                          fontFamily: 'Inter, sans-serif',
                          transition: 'background 0.15s',
                        }}
                      >
                        {loading === pack.usd ? (
                          <div style={{
                            width: 13, height: 13,
                            border: '2px solid #D4D4D8',
                            borderTopColor: '#18181B',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                          }} />
                        ) : (
                          <><Zap size={12} /> Buy</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <p style={{ fontSize: 12, color: '#EF4444', marginTop: 14, textAlign: 'center' }}>
                {error}
              </p>
            )}

            <p style={{
              fontSize: 11, color: '#A1A1AA', marginTop: 20,
              textAlign: 'center', lineHeight: 1.5,
            }}>
              Tests never expire · Secure payment
            </p>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
